import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ShieldCheck, ArrowRight, RefreshCw } from 'lucide-react';

const AdminLoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    setErrorMessage('');
    const { email, password } = formData;

    if (!email || !password) {
      setErrorMessage('Please enter both email address and password');
      return;
    }

    setLoading(true);

    try {
      const trimmedEmail = email.trim().toLowerCase();

      // Authenticate strictly against the PostgreSQL database backend
      const response = await fetch('https://api.vanyacollectionsrajahmundry.com/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, password }),
      });

      const data = await response.json().catch(() => ({}));

      setLoading(false);

      if (response.ok && (data.user || data.admin)) {
        // Clear any legacy custom credentials cache
        try {
          localStorage.removeItem('admin_custom_credentials');
        } catch (e) {}

        const adminUser = data.admin || data.user;
        const adminSession = {
          id: adminUser.id,
          email: adminUser.email || trimmedEmail,
          fullName: adminUser.fullName || adminUser.full_name || 'Administrator',
          role: 'Super Administrator',
          loginTime: new Date().toISOString(),
        };
        localStorage.setItem('admin', JSON.stringify(adminSession));
        navigate('/admin/products');
      } else {
        // Database rejected the password (e.g., old password entered after change)
        setErrorMessage(data.message || 'Invalid email or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setLoading(false);
      setErrorMessage('Unable to connect to the authentication server. Please try again.');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Brand Logo & Header */}
        <div style={styles.brandSection}>
          <div style={styles.logoMedallion}>
            <img
              src="/vanyalogo.png"
              alt="Vanya Collections"
              style={styles.logoImg}
            />
          </div>
          <h1 style={styles.brandTitle}>Vanya Collections</h1>
          <span style={styles.panelTag}>ADMINISTRATIVE PORTAL</span>
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div style={styles.errorBox}>
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleLogin} style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Admin Email Address</label>
            <div style={styles.inputWrapper}>
              <Mail size={17} style={styles.inputIcon} />
              <input
                type="email"
                placeholder="admin@vanyacollections.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Password</label>
            <div style={styles.inputWrapper}>
              <Lock size={17} style={styles.inputIcon} />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                style={styles.input}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.togglePasswordBtn}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitBtn,
              opacity: loading ? 0.75 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? (
              <>
                <RefreshCw size={16} className="spin-icon" />
                <span>Authenticating...</span>
              </>
            ) : (
              <>
                <span>Sign In to Dashboard</span>
                <ArrowRight size={17} />
              </>
            )}
          </button>
        </form>

        <div style={styles.footerNote}>
          <ShieldCheck size={14} color="#059669" />
          <span>Encrypted Authorized Access Only</span>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin-icon {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0d251e 0%, #14352b 50%, #0d251e 100%)',
    fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
    padding: '20px',
    boxSizing: 'border-box',
  },
  card: {
    background: '#ffffff',
    padding: '44px 38px',
    borderRadius: '24px',
    boxShadow: '0 20px 45px rgba(0,0,0,0.35)',
    width: '100%',
    maxWidth: '420px',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    border: '1px solid rgba(212, 175, 55, 0.3)',
  },
  brandSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '26px',
    textAlign: 'center',
  },
  logoMedallion: {
    width: '84px',
    height: '84px',
    borderRadius: '50%',
    background: '#0d251e',
    border: '2px solid #D4AF37',
    boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
    overflow: 'hidden',
  },
  logoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: 'scale(1.08)',
  },
  brandTitle: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#14352b',
    margin: '0 0 4px 0',
    letterSpacing: '-0.3px',
  },
  panelTag: {
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '2px',
    color: '#b89326',
    backgroundColor: '#fdf8ea',
    padding: '3px 10px',
    borderRadius: '12px',
    border: '1px solid #fae8b2',
  },
  errorBox: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#991b1b',
    padding: '12px 14px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '18px',
    fontWeight: '500',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '12.5px',
    fontWeight: '700',
    color: '#334155',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: '14px',
    color: '#94a3b8',
    pointerEvents: 'none',
  },
  input: {
    width: '100%',
    padding: '12px 40px 12px 42px',
    borderRadius: '10px',
    border: '1.5px solid #cbd5e1',
    fontSize: '14px',
    color: '#0f172a',
    backgroundColor: '#ffffff',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  togglePasswordBtn: {
    position: 'absolute',
    right: '12px',
    background: 'transparent',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
  },
  submitBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '14px 20px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #14352b 0%, #1f4a3c 100%)',
    color: '#D4AF37',
    border: '1px solid #14352b',
    fontSize: '14px',
    fontWeight: '800',
    cursor: 'pointer',
    marginTop: '6px',
    boxShadow: '0 6px 18px rgba(20, 53, 43, 0.3)',
    letterSpacing: '0.3px',
  },
  footerNote: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    marginTop: '24px',
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '500',
  },
};

export default AdminLoginPage;